import { ArrowLeft, LogIn, Mail } from '@tamagui/lucide-icons';
import { ShowPasswordInputIcon } from '@unistocks/components/basic/input';
import { SubmitButtons } from '@unistocks/components/button/submit-buttons';
import { Form } from '@unistocks/components/form/form';
import { requiredSchema } from '@unistocks/components/form/validators';
import { Anchor } from '@unistocks/components/text/link';
import { AuthFormProps } from '@unistocks/features/authentication/auth-form-props';
import { t } from '@unistocks/localization';
import { Link } from 'expo-router';
import React, { useMemo, useState } from 'react';
import { z } from 'zod';

export type LoginFormProps = AuthFormProps<LoginFormData>;

export interface LoginFormData {
  username: string;
  password: string;
}

export const LoginForm: React.FC<LoginFormProps> = ({ onSubmit, toggle }) => {
  const schema = useMemo(
    () =>
      z.object({
        username: requiredSchema(
          z.email(t('::ThisFieldIsNotAValidEmailAddress.')),
          undefined,
          t('::The {0} field is required.', { 0: t('AbpAccount::EmailAddress') })
        ),
        password: z.string().nullish()
      }),
    []
  );
  const [showPassword, setShowPassword] = useState(false);

  return (
    <Form
      schema={schema}
      onSubmit={onSubmit}
      customTrigger={
        <SubmitButtons
          cancelButtonIcon={ArrowLeft}
          cancelButtonText={t('::Back')}
          onCancel={toggle}
          submitButtonIcon={LogIn}
          submitButtonText={t('AbpAccount::Login')}
        />
      }
    >
      <Form.Input
        name={'username'}
        label={t('AbpAccount::EmailAddress')}
        keyboardType={'email-address'}
        textContentType={'username'}
        autoCapitalize={'none'}
        iconAfter={<Mail />}
      />

      <Form.Input
        name={'password'}
        label={t('AbpAccount::Password')}
        type={showPassword ? 'text' : 'password'}
        textContentType={'password'}
        autoCapitalize={'none'}
        returnKeyType={'send'}
        iconAfter={
          <ShowPasswordInputIcon showPassword={showPassword} setShowPassword={setShowPassword} />
        }
      />

      <Link asChild href={'/(auth)/forgot-password'}>
        <Anchor mb={'$3'}>{t('AbpAccount::ForgotPassword')}</Anchor>
      </Link>
    </Form>
  );
};

LoginForm.displayName = 'LoginForm';
